// @flow

import { parseMonetaryValue } from 'utils/parsers';
import type {
  AgingMismatch,
  AmountColumn,
  ColumnKey,
  FormValues,
  GridLayout,
  GridSection,
  LineFields,
  Rd356010Form,
  Rd356010Line,
} from './types';

export const FIELD_PREFIX = '0';

export const HEADER_FIELDS = Object.freeze({
  projectName: 'header.projectName',
  borrowerName: 'header.borrowerName',
  borrowerId: 'header.borrowerId',
  projectNumber: 'header.projectNumber',
});

const COLUMN_KEYS: Array<ColumnKey> = [
  'identifyLabel',
  'currentYear',
  'priorYear',
  'comment',
];

const AMOUNT_COLUMNS: Array<AmountColumn> = ['currentYear', 'priorYear'];

// The paper form's liabilities half starts at line 22.
const FIRST_LIABILITY_LINE_ID = '22';

const toCents = (value: number): number => Math.round(value * 100);

const roundToCents = (value: number): number => toCents(value) / 100;

const isBlank = (value: mixed): boolean =>
  value === null || value === undefined || String(value).trim() === '';

// Form values are typed `mixed`; the parser takes a number or a string.
const toParserInput = (value: mixed): number | string =>
  typeof value === 'number' ? value : String(value);

const disabledKeysFor = (line: Rd356010Line): Array<ColumnKey> => {
  if (line.kind === 'total') {
    return COLUMN_KEYS;
  }
  return line.identifyLabel === null || line.identifyLabel === undefined
    ? ['identifyLabel']
    : [];
};

/**
 * The TableBudget sections for the form, split into the paper form's two
 * halves, and the form field name of every cell.
 */
export const buildGridLayout = (
  lines: Array<Rd356010Line>,
  sectionHeaders: { assets: string, liabilities: string },
): GridLayout => {
  const splitAt = lines.findIndex(
    (line) => line.lineId === FIRST_LIABILITY_LINE_ID,
  );
  const halves = [
    { header: sectionHeaders.assets, lines: lines.slice(0, splitAt) },
    { header: sectionHeaders.liabilities, lines: lines.slice(splitAt) },
  ];

  const fieldsByLineId = {};
  const sections: Array<GridSection> = halves.map((half, sectionIndex) => ({
    header: half.header,
    rows: half.lines.map((line, rowIndex) => {
      fieldsByLineId[line.lineId] = COLUMN_KEYS.reduce(
        (fields: LineFields, key) => ({
          ...fields,
          [key]: [FIELD_PREFIX, sectionIndex, rowIndex, key].join('-'),
        }),
        {},
      );
      return {
        label: `${line.lineId}. ${line.label}`,
        disabledKeys: disabledKeysFor(line),
      };
    }),
  }));

  return { sections, fieldsByLineId };
};

// One rounding rule for showing, totalling and comparing amounts: round to
// cents, then print. The backend's PDF uses the same rule, so the grid shows
// what the PDF prints (toFixed alone would show 2.675 as 2.67, not 2.68).
const formatAmount = (value: ?number): string =>
  typeof value === 'number' ? roundToCents(value).toFixed(2) : '';

/** The form's starting values: the loaded header and every cell. */
export const toFormValues = (
  form: Rd356010Form,
  layout: GridLayout,
): FormValues =>
  form.lines.reduce(
    (values: FormValues, line) => {
      const fields = layout.fieldsByLineId[line.lineId];
      return {
        ...values,
        [fields.identifyLabel]: line.identifyLabel ?? '',
        [fields.currentYear]: formatAmount(line.currentYear),
        [fields.priorYear]: formatAmount(line.priorYear),
        [fields.comment]: line.comment ?? '',
      };
    },
    {
      header: {
        projectName: form.header.projectName,
        borrowerName: form.header.borrowerName,
        borrowerId: form.header.borrowerId,
        projectNumber: form.header.projectNumber,
      },
    },
  );

const readAmount = (values: FormValues, fieldName: string): ?number =>
  isBlank(values[fieldName])
    ? null
    : parseMonetaryValue(toParserInput(values[fieldName]));

/**
 * Every total line's value from the detail cells, using the formulas the
 * backend sends. Empty cells count as zero; totals are rounded to cents. The
 * backend recomputes these before generating, so they are display only.
 */
export const computeTotals = (
  lines: Array<Rd356010Line>,
  layout: GridLayout,
  values: FormValues,
  column: AmountColumn,
): { [lineId: string]: number } =>
  lines.reduce((totals, line) => {
    const { formula } = line;
    if (line.kind !== 'total' || !formula) {
      return totals;
    }
    // Each line counts at the cents it prints, as the backend's PDF totals do.
    const valueOf = (lineId: string): number =>
      totals[lineId] ??
      roundToCents(
        readAmount(values, layout.fieldsByLineId[lineId]?.[column] ?? '') ?? 0,
      );
    const added = formula.add.reduce((sum, id) => sum + valueOf(id), 0);
    const subtracted = formula.subtract.reduce(
      (sum, id) => sum + valueOf(id),
      0,
    );
    return { ...totals, [line.lineId]: roundToCents(added - subtracted) };
  }, {});

/** Total cells whose shown value differs from the computed total. */
export const totalsToUpdate = (
  lines: Array<Rd356010Line>,
  layout: GridLayout,
  values: FormValues,
): Array<{ fieldName: string, value: string }> =>
  AMOUNT_COLUMNS.flatMap((column) => {
    const totals = computeTotals(lines, layout, values, column);
    return Object.keys(totals)
      .map((lineId) => ({
        fieldName: layout.fieldsByLineId[lineId][column],
        value: formatAmount(totals[lineId]),
      }))
      .filter(({ fieldName, value }) => values[fieldName] !== value);
  });

/**
 * Aging sub-lines (under 7 and 22) that do not add up to their line. Only
 * informational: generation is never blocked, and a column whose sub-lines are
 * all empty is not checked.
 */
export const findAgingMismatches = (
  lines: Array<Rd356010Line>,
  layout: GridLayout,
  values: FormValues,
): Array<AgingMismatch> => {
  const parentIds = Array.from(
    new Set(lines.map((line) => line.parentLineId).filter(Boolean)),
  );
  return parentIds.flatMap((parentId) =>
    AMOUNT_COLUMNS.reduce((mismatches, column) => {
      const sublineAmounts = lines
        .filter((line) => line.parentLineId === parentId)
        .map((line) =>
          readAmount(values, layout.fieldsByLineId[line.lineId][column]),
        );
      if (sublineAmounts.every((amount) => amount === null)) {
        return mismatches;
      }
      const sublineTotal = roundToCents(
        sublineAmounts.reduce((sum, amount) => sum + (amount ?? 0), 0),
      );
      const lineTotal = roundToCents(
        readAmount(values, layout.fieldsByLineId[parentId][column]) ?? 0,
      );
      return toCents(sublineTotal) === toCents(lineTotal)
        ? mismatches
        : [
            ...mismatches,
            { lineId: parentId, column, sublineTotal, lineTotal },
          ];
    }, []),
  );
};

// An unedited cell sends back the exact loaded number (which can carry more
// than two decimals from Intacct), so the XML matches the 3560-10 report's.
const amountForPayload = (loaded: ?number, value: mixed): ?number => {
  if (isBlank(value)) {
    return null;
  }
  const entered = parseMonetaryValue(toParserInput(value));
  return typeof loaded === 'number' &&
    formatAmount(loaded) === formatAmount(entered)
    ? loaded
    : entered;
};

/** The generate request: the loaded form with the user's edits applied. */
export const toGeneratePayload = (
  form: Rd356010Form,
  layout: GridLayout,
  values: FormValues,
): Rd356010Form => {
  const header: Object = values.header ?? {};
  return {
    asOfDate: form.asOfDate,
    header: {
      ...form.header,
      projectName: String(header.projectName ?? ''),
      borrowerName: String(header.borrowerName ?? ''),
      borrowerId: String(header.borrowerId ?? ''),
      projectNumber: String(header.projectNumber ?? ''),
    },
    lines: form.lines.map((line) => {
      const fields = layout.fieldsByLineId[line.lineId];
      return {
        ...line,
        currentYear: amountForPayload(
          line.currentYear,
          values[fields.currentYear],
        ),
        priorYear: amountForPayload(line.priorYear, values[fields.priorYear]),
        comment: String(values[fields.comment] ?? ''),
        identifyLabel:
          line.identifyLabel === null || line.identifyLabel === undefined
            ? null
            : String(values[fields.identifyLabel] ?? ''),
      };
    }),
  };
};
